/**
 * GrossComparisonCards Component
 *
 * Displays three comparison cards:
 * 1. Current Year Gross at this point
 * 2. Last Year Gross at same point (same days remaining)
 * 3. 3-Year Average at same point
 *
 * Supports department filtering - shows department-specific gross when a
 * department is selected, or total gross when "ALL" is selected.
 */

import React from 'react';
import { DepartmentKey } from './DepartmentTabs';

interface GrossComparisonCardsProps {
  currentYear: number;
  currentGross: number | null;
  lastYearGross: number | null;
  lastYearFinalGross: number | null;
  lastYearHasData: boolean;
  averageGross: number | null;
  /** Weighted 50/30/20 average of final month gross from last 3 years */
  averageFinalGross?: number | null;
  averageYearsIncluded: number[];
  averageNote?: string;
  daysRemaining: number;
  selectedDepartment?: DepartmentKey;
  /** Unprocessed deals gross (sold but not yet accounting-processed) */
  unprocessedGross?: number | null;
  /** Trending end-of-month gross projection */
  trendingGross?: number | null;
  /** Projected monthly expenses from projection tile */
  projectedExpenses?: number | null;
  /** Trending profit = trending gross - projected expenses */
  trendingProfit?: number | null;
  /** Whether this is the current month (for showing trending data) */
  isCurrentMonth?: boolean;
  /** Hide expense and profit values (e.g., for MarketingManager) */
  hideExpenseProfit?: boolean;
  /** Optional 4th tile (projection) to render in the grid */
  projectionTile?: React.ReactNode;
}

const formatCurrency = (value: number | null): string => {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatCurrencyAbbreviated = (value: number | null): string => {
  if (value === null || value === undefined) return '—';
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  // Use K for values under $10M to preserve precision (e.g., $5,647K not $5.6M)
  if (absValue >= 10000000) {
    return `${sign}$${(absValue / 1000000).toFixed(1)}M`;
  }
  if (absValue >= 1000) {
    return `${sign}$${Math.round(absValue / 1000).toLocaleString()}K`;
  }
  return `${sign}$${Math.round(absValue)}`;
};

const formatVariance = (current: number | null, comparison: number | null): {
  value: string;
  percent: string;
  isPositive: boolean;
} => {
  if (current === null || comparison === null || comparison === 0) {
    return { value: '—', percent: '—', isPositive: true };
  }

  const diff = current - comparison;
  const percent = ((diff / comparison) * 100).toFixed(1);
  const isPositive = diff >= 0;

  return {
    value: formatCurrency(Math.abs(diff)),
    percent: `${isPositive ? '+' : '-'}${Math.abs(parseFloat(percent))}%`,
    isPositive,
  };
};

interface ComparisonCardProps {
  title: string;
  subtitle?: string;
  value: string;
  variance?: { value: string; percent: string; isPositive: boolean };
  note?: string;
  highlight?: boolean;
}

const ComparisonCard: React.FC<ComparisonCardProps> = ({
  title,
  subtitle,
  value,
  variance,
  note,
  highlight = false,
}) => {
  return (
    <div
      className={`rounded-lg shadow-sm border p-4 ${
        highlight ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'
      }`}
    >
      <div className="flex items-start justify-between">
        <div>
          <h4 className="text-sm font-medium text-gray-600">{title}</h4>
          {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
        </div>
        {variance && variance.value !== '—' && (
          <div
            className={`text-right ${
              variance.isPositive ? 'text-green-600' : 'text-red-600'
            }`}
          >
            <div className="text-sm font-medium">{variance.percent}</div>
            <div className="text-xs">{variance.isPositive ? '+' : '-'}{variance.value.replace('$', '')}</div>
          </div>
        )}
      </div>

      <div className={`mt-2 text-2xl font-bold ${highlight ? 'text-blue-700' : 'text-gray-900'}`}>
        {value}
      </div>

      {note && <p className="mt-1 text-xs text-gray-500">{note}</p>}
    </div>
  );
};

export const GrossComparisonCards: React.FC<GrossComparisonCardsProps> = ({
  currentYear,
  currentGross,
  lastYearGross,
  lastYearFinalGross,
  lastYearHasData,
  averageGross,
  averageFinalGross,
  averageYearsIncluded,
  averageNote,
  daysRemaining,
  selectedDepartment = 'ALL',
  unprocessedGross,
  trendingGross,
  projectedExpenses,
  trendingProfit,
  isCurrentMonth = false,
  hideExpenseProfit = false,
  projectionTile,
}) => {
  // currentGross already includes all sold deals (processed + unprocessed)
  // via the unified COALESCE query in DAILY_GROSS_SUMMARY
  const combinedGross = currentGross ?? 0;
  const hasUnprocessed = unprocessedGross !== null && unprocessedGross !== undefined && unprocessedGross !== 0;

  const lastYearVariance = formatVariance(combinedGross, lastYearGross);
  const averageVariance = formatVariance(combinedGross, averageGross);

  // Generate department label for subtitle
  const deptLabel = selectedDepartment !== 'ALL'
    ? ` (${selectedDepartment.replace('_', ' ')})`
    : '';

  // Use 4-column grid when projection tile exists, 3-column otherwise
  const gridCols = projectionTile
    ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
    : 'grid-cols-1 md:grid-cols-3';

  return (
    <div className={`grid ${gridCols} gap-4`}>
      {/* Current Year Card - with trending profit display */}
      <div className="rounded-lg shadow-sm border p-4 bg-blue-50 border-blue-200">
        <div className="flex items-start justify-between">
          <div>
            <h4 className="text-sm font-medium text-gray-600">{`${currentYear} Gross${deptLabel}`}</h4>
            <p className="text-xs text-gray-400 mt-0.5">{`${daysRemaining} working days remaining`}</p>
            {selectedDepartment === 'ALL' && (
              <p className="text-xs text-gray-400">Includes Others</p>
            )}
          </div>
          {/* Trending profit info - only for current month, hidden for MarketingManager */}
          {!hideExpenseProfit && isCurrentMonth && projectedExpenses !== null && projectedExpenses !== undefined && (
            <div className="text-right">
              <div className="text-xs text-gray-500">
                Exp: {formatCurrencyAbbreviated(projectedExpenses)}
              </div>
              {trendingProfit !== null && trendingProfit !== undefined && (
                <div className={`text-sm font-semibold ${
                  trendingProfit >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {formatCurrencyAbbreviated(trendingProfit)} <span className="text-xs font-normal">profit</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Current month with trending display */}
        {isCurrentMonth ? (
          <div className="mt-2 space-y-1">
            {/* Current (processed + unprocessed) */}
            <div className="flex justify-between items-center">
              <span className="text-xl font-bold text-blue-700">{formatCurrency(combinedGross)}</span>
              <span className="text-xs text-gray-500">Current</span>
            </div>
            {/* Trending gross */}
            {trendingGross !== null && trendingGross !== undefined && (
              <div className="flex justify-between items-center">
                <span className="text-xl font-bold text-blue-700">{formatCurrency(trendingGross)}</span>
                <span className="text-xs text-gray-500">Trending</span>
              </div>
            )}
            {/* Unprocessed breakdown if exists */}
            {hasUnprocessed && (
              <div className="border-t border-gray-200 pt-1 mt-1">
                <div className="flex justify-between items-center text-xs">
                  <span className="text-gray-500">Processed: {formatCurrency((currentGross ?? 0) - (unprocessedGross ?? 0))}</span>
                  <span className="text-amber-600">Unprocessed: {formatCurrency(unprocessedGross)}</span>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Historical month - simple display */
          hasUnprocessed ? (
            <div className="mt-2 space-y-1">
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">Processed Deals</span>
                <span className="text-sm font-medium text-gray-700">{formatCurrency((currentGross ?? 0) - (unprocessedGross ?? 0))}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">Unprocessed Deals</span>
                <span className="text-sm font-medium text-amber-600">{formatCurrency(unprocessedGross)}</span>
              </div>
              <div className="border-t border-gray-200 pt-1 mt-1">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-medium text-gray-600">Total</span>
                  <span className="text-xl font-bold text-blue-700">{formatCurrency(combinedGross)}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-2 text-2xl font-bold text-blue-700">
              {formatCurrency(currentGross)}
            </div>
          )
        )}
      </div>

      {/* Last Year Card */}
      <ComparisonCard
        title={`${currentYear - 1} Gross${deptLabel}`}
        subtitle={lastYearHasData ? `At ${daysRemaining} days remaining` : undefined}
        value={lastYearHasData ? formatCurrency(lastYearGross) : 'No Data'}
        variance={lastYearHasData ? lastYearVariance : undefined}
        note={
          lastYearHasData && lastYearFinalGross
            ? `Final month: ${formatCurrency(lastYearFinalGross)}`
            : undefined
        }
      />

      {/* 3-Year Average Card */}
      <ComparisonCard
        title={`3-Year Average${deptLabel}`}
        subtitle={averageYearsIncluded.length > 0
          ? `At ${daysRemaining} days remaining (50/30/20 weighted)`
          : undefined}
        value={averageGross !== null ? formatCurrency(averageGross) : 'No Data'}
        variance={averageGross !== null ? averageVariance : undefined}
        note={
          averageFinalGross !== null && averageFinalGross !== undefined
            ? averageNote
              ? `Final month avg: ${formatCurrency(averageFinalGross)} • ${averageNote}`
              : `Final month avg: ${formatCurrency(averageFinalGross)}`
            : averageNote
        }
      />

      {/* Projection Tile - 4th card */}
      {projectionTile}
    </div>
  );
};
